import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { exportDataToCSV } from '../utils/backupUtils';

const { FiSettings, FiDatabase, FiDownload, FiUpload, FiAlertTriangle } = FiIcons;

const Settings = () => {
  const fileInputRef = useRef(null);

  const [restoreStatus, setRestoreStatus] = useState(null); // 'idle' | 'preparing' | 'running' | 'success' | 'error'
  const [restoreErrors, setRestoreErrors] = useState([]);
  const [restoreProgress, setRestoreProgress] = useState({
    totalChunks: 0,
    completedChunks: 0,
  });
  const [restoreJob, setRestoreJob] = useState(null); // { jobId, plan: [{ collection, chunkIndex }...] }

  const handleExportData = async () => {
    try {
      await exportDataToCSV();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Basic validation
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      alert('Please upload a valid CSV file');
      event.target.value = '';
      return;
    }

    if (confirm('WARNING: Restoring a backup will REPLACE all current data (Members, Notes, Coaches, Goals) with the backup data. This cannot be undone. Are you sure?')) {
      await handleRestore(file);
      // Clear input so selecting the same file again works
      event.target.value = '';
    } else {
      event.target.value = '';
    }
  };

  const handleRestore = async (selectedFile) => {
    if (!selectedFile) return;

    setRestoreStatus('preparing');
    setRestoreErrors([]);
    setRestoreProgress({ totalChunks: 0, completedChunks: 0 });
    setRestoreJob(null);

    try {
      // STEP 1: INIT
      const formData = new FormData();
      formData.append('file', selectedFile);

      const initRes = await fetch('/api/restore?step=init', {
        method: 'POST',
        body: formData,
      });

      const initData = await initRes.json().catch(() => null);

      if (!initRes.ok || !initData?.success) {
        const msg =
          initData?.message ||
          `Restore init failed with status ${initRes.status}`;
        throw new Error(msg);
      }

      const { jobId, collections } = initData;

      // Build a flat plan of chunks to process
      const plan = [];
      const addChunks = (collectionKey, info) => {
        const count = info?.chunks || 0;
        for (let i = 0; i < count; i += 1) {
          plan.push({ collection: collectionKey, chunkIndex: i });
        }
      };

      addChunks('members', collections?.members || {});
      addChunks('notes', collections?.notes || {});
      addChunks('memberGoals', collections?.memberGoals || {});
      addChunks('memberMiniGoals', collections?.memberMiniGoals || {});
      addChunks('coaches', collections?.coaches || {});

      if (plan.length === 0) {
        throw new Error('No chunks to restore from backup.');
      }

      setRestoreJob({ jobId, plan });
      setRestoreProgress({ totalChunks: plan.length, completedChunks: 0 });
      setRestoreStatus('running');

      // STEP 2: CHUNKED RESTORE
      const aggregatedErrors = [];

      for (let i = 0; i < plan.length; i += 1) {
        const { collection, chunkIndex } = plan[i];

        try {
          const chunkRes = await fetch('/api/restore?step=chunk', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              jobId,
              collection,
              chunkIndex,
            }),
          });

          const chunkData = await chunkRes.json().catch(() => null);

          if (!chunkRes.ok || !chunkData?.success) {
            const errs = Array.isArray(chunkData?.errors)
              ? chunkData.errors
              : [
                  {
                    collection,
                    error:
                      chunkData?.message ||
                      `Chunk restore failed for ${collection}#${chunkIndex}`,
                  },
                ];
            aggregatedErrors.push(...errs);
          }
        } catch (chunkErr) {
          aggregatedErrors.push({
            collection,
            error:
              chunkErr?.message ||
              `Unexpected error restoring chunk for ${collection}#${chunkIndex}`,
          });
        }

        setRestoreProgress((prev) => ({
          totalChunks: prev.totalChunks,
          completedChunks: prev.completedChunks + 1,
        }));
      }

      if (aggregatedErrors.length > 0) {
        // eslint-disable-next-line no-console
        console.error('Restore finished with errors:', aggregatedErrors);
        setRestoreErrors(aggregatedErrors);
        setRestoreStatus('error');
      } else {
        setRestoreErrors([]);
        setRestoreStatus('success');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Restore failed:', err);
      setRestoreErrors([
        {
          collection: 'unknown',
          error: err?.message || 'Unknown restore error',
        },
      ]);
      setRestoreStatus('error');
    }
  };

  // Helper to check if an error is an auth error
  const isAuthError = (errString) => {
    if (!errString) return false;
    return (
      errString.includes("Authorization header") ||
      errString.includes("NCB_API_KEY") ||
      errString.includes("Instance")
    );
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">App Settings</h1>
          <p className="text-gray-600">System configuration and maintenance</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiDatabase} className="w-5 h-5 mr-2 text-primary-600" />
            Data Management
          </h2>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div>
                  <h3 className="font-medium text-gray-900">Export Data</h3>
                  <p className="text-sm text-gray-500">Download a comprehensive CSV backup of all system data</p>
                </div>
                <button
                  onClick={handleExportData}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium flex items-center transition-colors shadow-sm"
                >
                  <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                  Export Backup
                </button>
              </div>
            </div>

            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div>
                  <h3 className="font-medium text-red-900 flex items-center">
                    <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-2 text-red-600" />
                    Restore Data
                  </h3>
                  <p className="text-sm text-red-700 max-w-xl">
                    Upload a CSV backup to restore the system. <strong>Warning: This will completely replace all current data!</strong>
                  </p>
                </div>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".csv"
                    className="hidden"
                  />
                  <button
                    onClick={handleImportClick}
                    className="px-4 py-2 bg-white border border-red-300 rounded-lg text-red-700 hover:bg-red-50 font-medium flex items-center transition-colors shadow-sm"
                  >
                    <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
                    Restore Backup
                  </button>
                </div>
              </div>

              {/* Restore Status UI */}
              <div className="mt-4">
                {restoreStatus === 'preparing' && (
                  <p className="text-sm text-blue-600 animate-pulse">
                    Preparing backup restore... parsing file and creating chunks.
                  </p>
                )}

                {restoreStatus === 'running' && (
                  <div className="text-sm text-blue-700">
                    <p className="mb-1">
                      Restoring backup... This may take a moment.
                    </p>
                    {restoreProgress.totalChunks > 0 && (
                      <div className="w-full bg-blue-200 rounded-full h-2.5 mt-2 mb-1">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                          style={{ width: `${Math.round((restoreProgress.completedChunks / restoreProgress.totalChunks) * 100)}%` }}
                        ></div>
                      </div>
                    )}
                    <p className="text-xs">
                      Chunks completed: {restoreProgress.completedChunks} of{' '}
                      {restoreProgress.totalChunks}
                    </p>
                  </div>
                )}

                {restoreStatus === 'success' && (
                  <p className="text-sm text-green-700 font-medium">
                    Backup restored successfully. All chunks completed.
                  </p>
                )}

                {restoreStatus === 'error' && (
                  <div className="mt-2 p-3 rounded border border-red-300 bg-white text-sm text-red-800">
                    <p className="font-semibold mb-1">
                      Backup restore completed with one or more errors.
                    </p>
                    <p className="mb-2">
                      Some records may not have been created. See the details below,
                      and share them with your developer if you need help.
                    </p>

                    {/* Friendly Hint for Auth Errors */}
                    {restoreErrors.some(e => isAuthError(e.error)) && (
                      <div className="mb-3 p-3 bg-red-100 border-l-4 border-red-500 text-red-900 rounded text-sm">
                        <strong>Configuration Error:</strong> NoCodeBackend rejected the request because the Authorization or Instance header was missing. Please confirm NCB_API_KEY, NCB_INSTANCE_ID and NCB_BASE_URL are correctly set in Cloudflare Functions environment, then redeploy.
                      </div>
                    )}

                    {restoreErrors.length > 0 ? (
                      <div className="max-h-48 overflow-auto border-t border-red-200 pt-2 mt-2 text-[12px]">
                        {restoreErrors.slice(0, 20).map((err, index) => (
                          <div key={index} className="mb-2 pb-2 border-b border-red-100 last:border-0">
                            <div className="font-medium text-red-900">
                              Collection: <span className="font-mono bg-red-50 px-1 rounded">{err.collection || 'unknown'}</span>
                            </div>
                            {err.error && (
                              <div className="text-red-700 mt-1">Error: {err.error}</div>
                            )}
                            {err.status && (
                              <div className="text-red-600 text-xs">Status: {err.status}</div>
                            )}
                          </div>
                        ))}

                        {restoreErrors.length > 20 && (
                          <div className="mt-2 text-[11px] italic text-gray-500">
                            Showing first 20 errors out of {restoreErrors.length}. Check the
                            browser console for full details.
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-[12px] italic text-gray-500">
                        No detailed error information was provided by the server. Check the
                        browser console logs for more details.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2 text-primary-600" />
            Application Info
          </h2>
          <div className="space-y-2 text-sm text-gray-600">
            <p><span className="font-medium text-gray-900">Version:</span> 2.2.0 (KV Restore)</p>
            <p><span className="font-medium text-gray-900">Environment:</span> Production (Cloudflare Pages)</p>
            <p><span className="font-medium text-gray-900">Organization:</span> BPA Success Center</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Settings;